import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import VerificationInput from 'react-verification-input';
import styled from 'styled-components';

import { Button } from '../../../../components/Button';
import { Flex } from '../../../../components/Flex';
import { Text } from '../../../../components/Text';
import { WarningModal } from '../../../../components/WarningModal';
import { Modal } from '../../../../components/modal/Modal';
import { UpdateUserEmailVerifyCodeDto } from '../../../../model/user/dto/UpdateUserEmailVerifyCodeDto';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { userSliceSelectors } from '../../../../redux/user/user.selector';
import { userActions } from '../../../../redux/user/user.slice';
import { Rounded } from '../../../../theme/Rounded';
import { Spacing } from '../../../../theme/Spacing';
import { AtiraToast } from '../../../../utils/AtiraToast';

const InputWrapper = styled(Flex)<{ valid: boolean }>`
  width: 100%;
  margin-top: ${Spacing.l};

  .vi__container {
    width: 100% !important;
    direction: ltr;
  }

  .vi__character {
    border: ${({ theme, valid }) =>
      `2px solid ${valid ? theme.darkSub : theme.red}`} !important;
    background-color: ${({ theme }) => theme.transparent} !important;
    border-radius: ${Rounded.lg} !important;
  }

  .vi__character--filled {
    border-color: ${({ theme }) => theme.green} !important;
  }
`;

type SettingsEmailChangeVerificationModalProps = {
  open: boolean;
  onClose: VoidFunction;
  newEmail: string;
};

export const SettingsEmailChangeVerificationModal: React.FC<
  SettingsEmailChangeVerificationModalProps
> = ({ open, onClose, newEmail }) => {
  const [loading, setLoading] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);

  const { t } = useTranslation();

  const { control, getValues, watch, handleSubmit, reset } =
    useForm<UpdateUserEmailVerifyCodeDto>();

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const [token] = watch(['token']);

  const onLocalClose = () => {
    setWarningModalVisible(false);
    onClose();
    reset({ token: '' });
  };

  const onSubmit = async () => {
    try {
      setLoading(true);

      const token = getValues('token');

      if (!token) {
        return;
      }

      await dispatch(
        userActions.updateUserEmailVerifyCode({ userId, token }),
      ).unwrap();
      dispatch(userActions.getLoggedInUser({ userId }));

      AtiraToast.success(t('settings.general.changeEmail.verify.success'));

      onClose();

      reset({ token: '' });
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => setWarningModalVisible(true)}
        title={t('settings.general.changeEmail.verify.modal.title')}
      >
        <Flex width={'100%'} flexDirection="column" gap="m">
          <Text color="fgSecondary" align="center">
            <Trans
              i18nKey="settings.general.changeEmail.verify.modal.description"
              values={{ email: newEmail }}
              components={[<strong />]}
            />
          </Text>

          <Controller
            control={control}
            name="token"
            rules={{
              required: {
                value: true,
                message: t('common.this_field_required'),
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <InputWrapper valid={!error}>
                <VerificationInput
                  placeholder=""
                  onChange={onChange}
                  value={value}
                />
              </InputWrapper>
            )}
          />

          <Button
            onClick={handleSubmit(onSubmit, (e) => console.log(e))}
            disabled={!token}
            height="2.3rem"
            loading={loading}
          >
            {t('settings.general.changeEmail.verify.modal.button')}
          </Button>
        </Flex>
      </Modal>

      <WarningModal
        description={t('common.closeWarningModal.description')}
        open={warningModalVisible}
        onClose={() => setWarningModalVisible(false)}
        onConfirm={onLocalClose}
      />
    </>
  );
};
